import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import {
  Box, Heading, Flex, Select, Button, Text, HStack, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer,
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Page from '../components/ui/Page';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import StatusBadge from '../components/ui/StatusBadge';
import HelpTooltip from '../components/ui/HelpTooltip';
import SearchInput from '../components/filters/SearchInput';
import { useProjectContext } from '../context/ProjectContext';
import client from '../utils/fetchClient';

const PAGE_SIZE = 20;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const fmtDuration = (ms) => {
  if (!ms || Number.isNaN(Number(ms))) return '-';
  const s = Math.round(Number(ms) / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};

const fmtWhen = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString();
};

const runKind = (run) => {
  if (run.status === 'running') return 'running';
  if ((run.failedTests || 0) > 0 || run.status === 'failed') return 'failed';
  const cases = run.TestCases || run.testcases || [];
  if (cases.some((c) => c.status === 'flaky')) return 'flaky';
  return 'passed';
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'running', label: 'Running' },
  { key: 'passed', label: 'Passed' },
  { key: 'failed', label: 'Failed' },
  { key: 'flaky', label: 'Flaky' },
];

function Segmented({ value, onChange }) {
  return (
    <HStack spacing={0} bg="gray.100" border="1px solid" borderColor="gray.200" borderRadius="full" p="3px">
      {FILTERS.map((f) => {
        const active = f.key === value;
        return (
          <Button
            key={f.key}
            onClick={() => onChange(f.key)}
            variant="unstyled"
            h="28px"
            px={4}
            borderRadius="full"
            fontSize="12px"
            fontWeight={active ? '700' : '600'}
            color={active ? 'brand.600' : 'gray.500'}
            bg={active ? 'white' : 'transparent'}
            boxShadow={active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'}
            display="flex"
            alignItems="center"
          >
            {f.label}
          </Button>
        );
      })}
    </HStack>
  );
}

export default function TestRunsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const didInit = useRef(false);
  const { projects, selectedProject, setSelectedProject, effectiveProjectParam, loading: projectsLoading } = useProjectContext();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    if (didInit.current) return;
    const qpPage = searchParams.get('page');
    const qpQ = searchParams.get('q');
    const qpStatus = searchParams.get('status');
    if (qpPage) setPage(parseInt(qpPage, 10) || 1);
    if (qpQ) setQuery(qpQ);
    if (qpStatus && FILTERS.some((f) => f.key === qpStatus)) setStatusFilter(qpStatus);
    didInit.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const next = new URLSearchParams();
    if (page > 1) next.set('page', String(page));
    if (query) next.set('q', query);
    if (statusFilter !== 'all') next.set('status', statusFilter);
    setSearchParams(next, { replace: true });
  }, [page, query, statusFilter, setSearchParams]);

  const fetchRuns = useCallback((silent) => {
    if (!silent) { setLoading(true); setError(null); }
    const params = { page_size: PAGE_SIZE };
    if (effectiveProjectParam) params.project = effectiveProjectParam;
    if (page > 1) params.page = page;
    return client.get('/api/test-runs', { query: params })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.data || data?.runs || data?.results || []);
        const total = data?.pagination?.total ?? data?.total ?? list.length;
        setTotalPages(Math.max(1, Math.ceil(total / PAGE_SIZE)));
        setRuns(list);
        if (!silent) setLoading(false);
      })
      .catch(() => {
        if (!silent) {
          setRuns([]);
          setError('An error occurred while fetching test runs.');
          setLoading(false);
        }
      });
  }, [effectiveProjectParam, page]);

  useEffect(() => { fetchRuns(false); }, [fetchRuns]);

  // Live-patch rows in place via SSE instead of polling. A brand-new run
  // (not yet in `runs`) triggers one light background refetch to pick it up;
  // updates to a run already on the page just patch that row directly.
  useEffect(() => {
    const url = new URL(`${API_BASE_URL}/api/test-runs/live-stream`, window.location.origin);
    if (effectiveProjectParam) url.searchParams.set('project', effectiveProjectParam);
    const es = new EventSource(url.toString(), { withCredentials: true });

    const patchRow = (runId, patch) => {
      setRuns((prev) => {
        if (!prev.some((r) => r.id === runId)) return prev;
        return prev.map((r) => (r.id === runId ? { ...r, ...patch } : r));
      });
    };

    es.addEventListener('snapshot', (e) => {
      const { runs: running } = JSON.parse(e.data);
      (running || []).forEach((r) => patchRow(r.id, r));
    });
    es.addEventListener('started', () => fetchRuns(true));
    es.addEventListener('event', (e) => {
      const { runId, run } = JSON.parse(e.data);
      patchRow(runId, run);
    });
    es.addEventListener('finished', (e) => {
      const { runId, run } = JSON.parse(e.data);
      patchRow(runId, run);
    });

    return () => es.close();
  }, [effectiveProjectParam, fetchRuns]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (Array.isArray(runs) ? runs : [])
      .map((r) => ({ ...r, _kind: runKind(r) }))
      .filter((r) => (statusFilter === 'all' ? true : r._kind === statusFilter))
      .filter((r) => (q ? `${r.name || ''} ${r.branch || ''} ${r.commit || ''}`.toLowerCase().includes(q) : true));
  }, [runs, query, statusFilter]);

  if (loading || projectsLoading) return <LoadingState message="Loading test runs" minH="60vh" />;
  if (error) return <ErrorState message={error} onRetry={() => window.location.reload()} />;

  return (
    <Page maxW="7xl">
      <Breadcrumbs items={[{ label: 'Metrics', to: '/metrics' }, { label: 'Test runs' }]} />
      <Box mb={5}>
        <Heading fontWeight="800" letterSpacing="-0.5px" size="lg" color="gray.900" display="flex" alignItems="center">
          Test Runs
          <HelpTooltip label="Browse and filter test runs. Click a run to inspect its failures." href="https://docs.example.com/qa/test-runs" />
        </Heading>
        <Text fontSize="sm" color="gray.500">Every run published for the selected project</Text>
      </Box>

      <Flex align="center" gap={3} wrap="wrap" mb={4}>
        <SearchInput
          value={query}
          onChange={(v) => { setQuery(v); setPage(1); }}
          onClear={() => { setQuery(''); setPage(1); }}
          placeholder="Search name, branch or commit"
          size="sm"
          flex="1"
        />
        <Segmented value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} />
        <Box flex="1" />
        <Text fontSize="xs" color="gray.400">{rows.length} runs</Text>
      </Flex>

      <Flex mb={4} display={{ base: 'flex', md: 'none' }}>
        <Select
          value={selectedProject || 'ALL PROJECTS'}
          onChange={(e) => setSelectedProject(e.target.value)}
          maxW="220px"
          bg="white"
          size="sm"
          borderRadius="full"
        >
          <option value="ALL PROJECTS">All Projects</option>
          {projects.map((p, idx) => (
            <option key={(p.id ?? p) + '-' + idx} value={p.id ?? p}>{p.name ?? p}</option>
          ))}
        </Select>
      </Flex>

      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="16px" overflow="hidden">
        {rows.length === 0 ? (
          <Box p={10}>
            <EmptyState title="No test runs" subtitle="Nothing matches this filter." />
          </Box>
        ) : (
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Run</Th>
                  <Th>Result</Th>
                  <Th>Passed / failed</Th>
                  <Th>Duration</Th>
                  <Th>Env</Th>
                  <Th>Branch</Th>
                  <Th>Commit</Th>
                  <Th>When</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((r) => {
                  const running = r._kind === 'running';
                  const total = (r.passedTests || 0) + (r.failedTests || 0) + (r.skippedTests || 0) || 1;
                  const pct = (n) => `${((100 * (n || 0)) / total).toFixed(1)}%`;
                  const dot = r._kind === 'failed' ? 'status.fail' : r._kind === 'flaky' ? 'status.flaky' : r._kind === 'running' ? 'info.500' : 'status.pass';
                  const target = running ? `/test-runs/${r.id}/live` : `/test-runs/${r.id}`;
                  return (
                    <Tr
                      key={r.id}
                      _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                      onClick={() => navigate(target)}
                    >
                      <Td>
                        <HStack spacing={2.5}>
                          <Box
                            w="8px"
                            h="8px"
                            borderRadius="full"
                            bg={dot}
                            flexShrink={0}
                            sx={running ? { animation: 'pulse 1.4s infinite' } : undefined}
                          />
                          <Box>
                            <Text fontWeight="600" color="gray.900">{r.name || `Run #${r.id}`}</Text>
                            <Text fontSize="11px" color="gray.400">#{String(r.id).slice(0, 8)}</Text>
                          </Box>
                        </HStack>
                      </Td>
                      <Td><StatusBadge status={r._kind} /></Td>
                      <Td>
                        <Flex h="7px" w="130px" borderRadius="full" overflow="hidden" bg="gray.100">
                          <Box w={pct(r.passedTests)} bg="status.pass" />
                          <Box w={pct(r.failedTests)} bg="status.fail" />
                          <Box w={pct(r.skippedTests)} bg="status.skip" />
                        </Flex>
                        <Text fontSize="11px" color="gray.500">{r.passedTests || 0} / {r.failedTests || 0}</Text>
                      </Td>
                      <Td fontFamily="mono" fontSize="12px">{running ? '…' : fmtDuration(r.totalDuration)}</Td>
                      <Td>{r.environment || '-'}</Td>
                      <Td fontFamily="mono" fontSize="12px">{r.branch || '-'}</Td>
                      <Td fontFamily="mono" fontSize="12px" color="brand.600">{r.commit ? String(r.commit).slice(0, 8) : '-'}</Td>
                      <Td whiteSpace="nowrap" color="gray.500">{fmtWhen(r.startTime)}</Td>
                      <Td onClick={(e) => e.stopPropagation()}>
                        <Button as="a" href={target} onClick={(e) => { e.preventDefault(); navigate(target); }} size="xs" variant="outline" borderRadius="full">
                          {running ? 'Watch live' : 'Open'}
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Flex mt={6} align="center" justify="center" gap={4}>
        <Button size="sm" variant="outline" borderRadius="full" onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page === 1}>
          Previous
        </Button>
        <Text fontSize="sm" color="gray.500">Page {page} of {totalPages}</Text>
        <Button size="sm" borderRadius="full" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} isDisabled={page >= totalPages}>
          Next
        </Button>
      </Flex>
    </Page>
  );
}
